import {
  Button,
  ButtonSelect,
  Icon,
  InputField,
  Menu,
} from "@saviynt/design-system";
import { FormattedMessage, useIntl } from "react-intl";
import { useTheme } from "@repo/mfe-core";
import { IntegrationsSortMenu } from "./IntegrationsSortMenu";

interface FilterOption {
  value: string;
  label: string;
  isChecked: boolean;
}

interface IntegrationsListControlsProps {
  searchValue: string;
  onSearchChange: (value: string) => void;
  showFilters: boolean;
  onToggleFilters: () => void;
  onSortChange: (field: string, direction: string) => void;
  connectionSourceOptions: FilterOption[];
  connectionStatusOptions: FilterOption[];
  sourceDisplayLabel: string;
  statusDisplayLabel: string;
  onConnectionSourceChange: (
    selectedOptions?: Array<{
      value: string;
      label: string;
    }>,
  ) => void;
  onConnectionStatusChange: (
    selectedOptions?: Array<{
      value: string;
      label: string;
    }>,
  ) => void;
}

export function IntegrationsListControls({
  searchValue,
  onSearchChange,
  showFilters,
  onToggleFilters,
  onSortChange,
  connectionSourceOptions,
  connectionStatusOptions,
  sourceDisplayLabel,
  statusDisplayLabel,
  onConnectionSourceChange,
  onConnectionStatusChange,
}: IntegrationsListControlsProps) {
  const { theme: currentTheme } = useTheme();
  const intl = useIntl();
  return (
    <div className="flex flex-col gap-0 py-4 px-12">
      <div className="flex items-center gap-2">
        <div className="w-1/2">
          <InputField
            id="integrations-search-input"
            name="search"
            placeholder={intl.formatMessage({
              id: "gs1/Zm",
              defaultMessage: "Search integrations by name",
              description: "Search integrations input placeholder",
            })}
            kind="search"
            value={searchValue}
            setValue={onSearchChange}
            prefixIcon={
              <Icon
                kind="Search"
                size="small"
                color="--color-foreground-neutral-subtle"
                dataTheme={currentTheme}
              />
            }
            dataTheme={currentTheme}
            data-testid="integrations-search-input"
            size="medium"
          />
        </div>
        <IntegrationsSortMenu onSortChange={onSortChange} />
        <Button
          kind="outlined"
          leftIcon={<Icon kind="Filter" size="small" />}
          onClick={onToggleFilters}
          size="medium"
        >
          <FormattedMessage
            id="ntGwfy"
            defaultMessage="Filter"
            description="Filter button label"
          />
        </Button>
      </div>
      {/* Filter Pills - shown when filter button is clicked */}
      {showFilters && (
        <div className="flex gap-2 items-center mt-2 py-2">
          <Menu
            kind="simple"
            size="medium"
            isPortalEnabled
            options={connectionSourceOptions.map((option) => ({
              value: option.value,
              label: option.label,
            }))}
            onChange={(option) => {
              // Single select: replace previous selection
              const currentSelected = connectionSourceOptions.find(
                (opt) => opt.isChecked,
              );

              if (currentSelected?.value === option.value) {
                // If clicking the same option and it's not "All", clear selection
                // If it's "All", keep it selected
                if (option.value === "all") {
                  onConnectionSourceChange([{ value: "all", label: "All" }]);
                } else {
                  onConnectionSourceChange(undefined);
                }
              } else {
                // Replace with new selection
                onConnectionSourceChange([
                  { value: option.value, label: option.label },
                ]);
              }
            }}
            trigger={<ButtonSelect kind="pill" label={sourceDisplayLabel} />}
          />
          <Menu
            kind="simple"
            isPortalEnabled
            options={connectionStatusOptions.map((option) => ({
              value: option.value,
              label: option.label,
            }))}
            onChange={(option) => {
              // Single select: replace previous selection
              const currentSelected = connectionStatusOptions.find(
                (opt) => opt.isChecked,
              );

              if (currentSelected?.value === option.value) {
                // If clicking the same option and it's not "All", clear selection
                // If it's "All", keep it selected
                if (option.value === "all") {
                  onConnectionStatusChange([{ value: "all", label: "All" }]);
                } else {
                  onConnectionStatusChange(undefined);
                }
              } else {
                // Replace with new selection
                onConnectionStatusChange([
                  { value: option.value, label: option.label },
                ]);
              }
            }}
            trigger={<ButtonSelect kind="pill" label={statusDisplayLabel} />}
          />
        </div>
      )}
    </div>
  );
}
